/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-03 14:54:33
 **/

import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { useTheme } from "@emotion/react";
import LottiePlayer from "@rsupport/lottieplayer";
import styled from "@emotion/styled";

import { ChronoUtils, Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Button, Column, Icon, Row, Tooltip, TypographyTooltip, useToast } from "@/frameworks/designStuff/components";
import { JotaiStore } from "@/frameworks/libraries/commons";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { APIErrorCode, POPOVER_ID } from "@/frameworks/core/commons";
import { orderCallUser, removeMeFromList } from "@/frameworks/core/utils";
import { usePhoneCall, useSidebarActions, PhoneCallAtoms, usePopover } from "@/frameworks/core/hooks";
import { Avatar, CallAvatar } from "@/frameworks/core/components";
import { useChatRoomActions } from "@/frameworks/core/chat";
import { Maybe } from "@/frameworks/libraries/components";

import { ScreenShareVideo, UserVideoWithAvatar } from "../index";
import * as S from "./emotion";
import { AddCalleesPopup, DialingState } from "../commons";
import voiceRecordingCall from "@/assets/lotties/media/voice_recording_call.json";
import withRenderDetect from "@/frameworks/core/utils/withRenderDetect";

// const LOGTAG = "#ScreenShare#";
const optionsRecording = {
	loop: true,
	autoplay: false,
	animationData: voiceRecordingCall,
	rendererSettings: {
		preserveAspectRatio: "xMidYMid slice"
	}
};

const SCREEN_SHARE_HEADER_HEIGHT = 192;

export function ScreenShare({ callID, callerID, dominantUser }: PushServices.Call.PhoneCallPopupItem) {
	const phoneCall = usePhoneCall();
	const toast = useToast();
	const popover = usePopover();
	const sidebarActions = useSidebarActions();
	const [selectedUser, setSelectedUser] = useState({
		userID: "",
		isScreen: false
	});
	const callUsers = phoneCall.state.users(callID)[0];
	const { colors } = useTheme();
	const userList = phoneCall.get_users(callID);
	const userIDList = phoneCall.state.participatedUserList(callID);
	const myMediaState = phoneCall.get_user_media(callID, Configure.userID);
	const call = useAtomValue(PhoneCallAtoms.Call(callID ?? ""), { store: JotaiStore });
	const chatRoomActions = useChatRoomActions();
	const callStatus = phoneCall.state.status(callID)[0];

	const [isExpand, setIsExpand] = useState(false);
	useEffect(() => {
		if (selectedUser.userID === "" && dominantUser) {
			setSelectedUser(dominantUser);
			return;
		}
		const userWithScreenShare =
			Object.entries(userList).map(([userID, userMediaInfo]) => {
				if (userMediaInfo.media?.screenShare) return userID;
			})[0] || "";
		if (userWithScreenShare?.length > 0) {
			setSelectedUser({ userID: userWithScreenShare, isScreen: true });
		} else {
			setSelectedUser((prev) => {
				if (prev?.userID === Configure.userID) {
					return { userID: Configure.userID, isScreen: false };
				} else {
					return { userID: prev?.userID || "", isScreen: false };
				}
			});
		}
	}, [myMediaState?.screenShare, userList]);

	useEffect(() => {
		if (call.status) {
			["ENDED", "REJECTED"].includes(call.status) && phoneCall.popup.hide(callID);
		}
	}, [call]);

	useEffect(() => {
		if (sidebarActions.isOpen("Phone")) {
			sidebarActions.hide("Phone");
		}
	}, []);

	const handleAddUser = async (ev: React.MouseEvent<HTMLButtonElement>) => {
		popover.show({
			popoverID: POPOVER_ID.ADD_CALL_USER,
			content: <AddCalleesPopup callID={callID} />,
			anchorEl: ev.currentTarget,
			direction: {
				x: "right",
				y: "top"
			}
		});
	};
	const openDM = async () => {
		try {
			const data = await chatRoomActions.dm.create({
				userIDs: [...participatedUserList]
			});
			if (!data) return;
			chatRoomActions.common.open({ channelID: data.id, type: "DM" });
		} catch (err) {
			const error = err as RestAPIError;
			switch (error.code) {
				case APIErrorCode.EXIST_SAME_CHAT_MEMBER: {
					const { roomId } = error.data.payload;
					// const DMRoom = chatRoomListActions.getDMRoom(roomId);
					// if (DMRoom?.hide) {
					// 	chatRoomActions.dm.hide({ channelID: roomId, hide: false });
					// }
					chatRoomActions.common.open({ channelID: roomId, type: "DM" });
				}
			}
		}
	};

	/**
	 * Update 06/01/2025:  When you end a call, the profile of the person who has already left should not appear on the screen.
	 *  @Ticket: 231955
	 */
	const userIdListWithStatusNotEqualsEnded = !callUsers
		? []
		: Object.values(callUsers)
				?.filter((user) => user.status !== "ENDED")
				?.map((user) => user.userId ?? "");
	const userIdListWhenEndCall =
		userIdListWithStatusNotEqualsEnded?.length == 0 ? [Configure.userID] : userIdListWithStatusNotEqualsEnded;
	const participatedUserList = callStatus !== "END" ? userIDList : userIdListWhenEndCall;
	const participatedUserWithoutMe = removeMeFromList(participatedUserList);
	const requestUserList =
		call.callUsers &&
		Object.values(call.callUsers)
			.filter((user) => user.status === "REQUESTED")
			.map((user) => user.userId ?? "");
	const recordTime = useAtomValue(PhoneCallAtoms.RecordTime(callID ?? ""), { store: JotaiStore });
	return (
		<S.FullSizeWrapper>
			<S.FullSizeHeader horizonAlign="distribute" verticalAlign="bottom">
				<Column style={{ height: "100%" }} gap={10}>
					<Maybe test={!isExpand}>
						<Row horizonAlign="distribute" style={{ width: "100%" }} verticalAlign="center">
							<Row gap={16} verticalAlign="center">
								<Maybe test={removeMeFromList(participatedUserList).length === 1}>
									<Avatar
										id={participatedUserWithoutMe?.[0]}
										kind="people_32"
										showMiniProfile={false}
									/>
									<Maybe.Fallback>
										<Row
											style={{
												backgroundColor: colors.accent_40_100,
												width: "32px",
												height: "32px",
												borderRadius: "50%"
											}}
											verticalAlign="center"
											horizonAlign="center"
										>
											<Icon name="People24" size={16} fill="white" />
										</Row>
									</Maybe.Fallback>
								</Maybe>
								<Column>
									<Row style={{ width: "170px" }}>
										<TypographyTooltip.p
											tooltipProps={{
												hasIcon: false
											}}
											componentProps={{
												typoColor: "neutral_00_100",
												typoSize: "Title1_B",
												ellipsis: true,
												maxLine: 1,
												stretch: true,
												align: "left",
												width: "100px"
											}}
										>
											{participatedUserWithoutMe
												.map((userID) => {
													const userInfo = callUsers?.[userID];
													return userInfo?.name;
												})
												.join(", ")}
										</TypographyTooltip.p>
									</Row>
								</Column>
							</Row>
						</Row>
					</Maybe>
					<Row style={{ padding: "4px 0" }} gap={8} horizonAlign="distribute" verticalAlign="center">
						<Row gap={4} style={{ height: "100%" }} verticalAlign="center">
							<Button.Icon
								name={isExpand ? "Up16" : "Down16"}
								size="medium_18"
								filled="tertiary"
								fill="neutral_80_100"
								onClick={() => setIsExpand((prev) => !prev)}
							/>
							<Maybe test={isExpand}>
								<Row
									horizonAlign="distribute"
									style={{ width: "230px", marginLeft: "8px" }}
									verticalAlign="center"
								>
									<Row gap={16} verticalAlign="center">
										<Maybe test={removeMeFromList(participatedUserList).length === 1}>
											<Avatar
												id={participatedUserWithoutMe?.[0]}
												kind="people_32"
												showMiniProfile={false}
											/>
											<Maybe.Fallback>
												<Row
													style={{
														backgroundColor: colors.accent_40_100,
														width: "32px",
														height: "32px",
														borderRadius: "50%"
													}}
													verticalAlign="center"
													horizonAlign="center"
												>
													<Icon name="People24" size={16} fill="white" />
												</Row>
											</Maybe.Fallback>
										</Maybe>
										<Row style={{ width: "170px" }}>
											<TypographyTooltip.p
												tooltipProps={{
													hasIcon: false
												}}
												componentProps={{
													typoColor: "neutral_00_100",
													typoSize: "Title1_B",
													ellipsis: true,
													maxLine: 1,
													stretch: true,
													align: "left",
													width: "100px"
												}}
											>
												{participatedUserWithoutMe
													.map((userID) => {
														const userInfo = callUsers?.[userID];
														return userInfo?.name;
													})
													.join(", ")}
											</TypographyTooltip.p>
										</Row>
									</Row>
									<Delimiter />
								</Row>
							</Maybe>
							<Button.Icon
								name={myMediaState?.video === "on" ? "CamOn24" : "CamOff24"}
								onClick={() =>
									phoneCall?.camera_update(
										callID,
										Configure.userID,
										myMediaState?.video === "on" ? "off" : "on"
									)
								}
								size="large"
								filled="transparent"
								fill="neutral_00_100"
							/>
							<Button.Icon
								name={myMediaState?.audio === "on" ? "MicOn24" : "MicOff24"}
								size="large"
								filled="transparent"
								fill="neutral_00_100"
								onClick={() =>
									phoneCall?.microphone_update(
										callID,
										Configure.userID,
										myMediaState?.audio === "on" ? "off" : "on"
									)
								}
							/>
							{/* feature not yet developed, will show temporary UI */}
							<Button.Icon name={"SpeakerOn24"} size="large" filled="transparent" fill="neutral_00_100" />
							<StyledLottieButton
								disabled={callStatus !== "ONGOING"}
								onClick={() => {
									if (myMediaState?.isRecording) {
										phoneCall.record_update(callID, "stop");
										toast.show({
											type: "system",
											message: _T(
												"통화 녹음이 종료되었습니다. 저장된 파일은 파일함에서 확인 가능합니다."
											)
										});
									} else {
										phoneCall.record_update(callID, "start");
										toast.show({ type: "system", message: _T("통화 녹음이 시작되었습니다.") });
									}
								}}
								style={{
									backgroundColor: "transparent",
									borderRadius: "20px",
									width: 32,
									height: 32,
									opacity: callStatus !== "ONGOING" ? 0.4 : 1
								}}
							>
								{myMediaState?.isRecording ? (
									<Tooltip
										message={_T("녹음중 {{time}}", {
											time: ChronoUtils.calculateRecording(recordTime ?? 0)
										})}
										hasIcon={false}
									>
										<LottiePlayer
											isStopped={!myMediaState?.isRecording}
											isPaused={!myMediaState?.isRecording}
											options={optionsRecording}
											height={24}
											width={24}
										/>
									</Tooltip>
								) : (
									<LottiePlayer
										isStopped={!myMediaState?.isRecording}
										isPaused={!myMediaState?.isRecording}
										options={optionsRecording}
										height={24}
										width={24}
									/>
								)}
							</StyledLottieButton>
							<Button.Icon
								name="ScreenShare24"
								size="large"
								filled="transparent"
								fill="neutral_00_100"
								disabled={callStatus !== "ONGOING"}
								onClick={() => phoneCall.screen_update(callID, Configure.userID, "start")}
							/>
							{callerID === Configure.userID && (
								<Button.Icon
									name="CallAdd24"
									size="large"
									filled="transparent"
									fill="neutral_00_100"
									disabled={participatedUserList.length === 4 || callStatus === "READY"}
									onClick={handleAddUser}
								/>
							)}
							<Delimiter />
							<Button.Icon
								name="Chat0124"
								size="large"
								filled="transparent"
								fill="neutral_00_100"
								disabled={
									userList &&
									Object.values(userList)?.filter((user) => user.status === "REQUESTED")?.length > 0
								}
								onClick={async () => await openDM()}
							/>
						</Row>
					</Row>
				</Column>
				<Row gap={12} style={{ margin: "0px 120px" }} horizonAlign="center">
					{participatedUserList &&
						orderCallUser(participatedUserList, call.callerId, requestUserList).map((userID) => {
							const userInfo = callUsers?.[userID];
							return (
								<>
									{selectedUser?.userID !== userID ||
									(selectedUser?.userID === userID && selectedUser.isScreen) ? (
										isExpand ? (
											<CallAvatar
												id={userID ?? ""}
												size="small"
												useStatus={false}
												callType={
													userInfo?.status === "REQUESTED"
														? "calling"
														: userInfo?.status === "REJECTED"
															? "no_response"
															: "none"
												}
											/>
										) : (
											<UserVideoWithAvatar
												callType={
													userID === Configure.userID
														? "none"
														: userInfo.status === "REQUESTED"
															? "calling"
															: userInfo.status === "REJECTED"
																? "no_response"
																: "none"
												}
												key={userID}
												callID={callID}
												userID={userID || ""}
												kind="people_80"
												style={{ width: "192px", height: "108px" }}
												isExpandIcon={true}
												onClick={() =>
													setSelectedUser({
														isScreen: false,
														userID: userID
													})
												}
											/>
										)
									) : null}
									{!selectedUser?.isScreen && callUsers?.[userID].media?.screenShare && (
										<ScreenShareVideo
											key={userID + "screenShare"}
											callID={callID}
											userID={userID || ""}
											style={{ width: "192px", height: "108px" }}
											isExpandIcon={true}
											onClick={() =>
												setSelectedUser({
													isScreen: true,
													userID: userID
												})
											}
										/>
									)}
								</>
							);
						})}
				</Row>
				<Column style={{ width: "345px", height: "100%" }} horizonAlign="right">
					<Maybe test={!isExpand}>
						<Row style={{ height: "30px" }}>
							<Icon
								name="Closemaxi24"
								size={20}
								fill={colors.neutral_00_88}
								onClick={() => phoneCall.popup.show({ callID, callerID, uiType: "floating" })}
							/>
						</Row>
					</Maybe>
					<Row verticalAlign="center" horizonAlign="center">
						<Row style={{ paddingRight: "20px" }}>
							<DialingState call={call} callID={callID} />
						</Row>
						<Button.Icon
							name="CallEnd24"
							filled="transparent"
							fill="neutral_00_100"
							size="large"
							style={{ width: "96px", height: "40px", backgroundColor: colors.error_60_100 }}
							onClick={() => {
								phoneCall.call_end(callID);
							}}
						/>
						<Maybe test={isExpand}>
							<Row style={{ marginLeft: 12 }}>
								<Icon
									name="Closemaxi24"
									size={20}
									fill={colors.neutral_00_88}
									onClick={() => phoneCall.popup.show({ callID, callerID, uiType: "floating" })}
								/>
							</Row>
						</Maybe>
					</Row>
				</Column>
			</S.FullSizeHeader>
			<Row
				style={{
					width: "100%",
					height: `calc( 100vh - ${SCREEN_SHARE_HEADER_HEIGHT}px )`,
					backgroundColor: colors.neutral_80_90,
					backdropFilter: "blur(20px)"
				}}
				horizonAlign="center"
			>
				{selectedUser?.isScreen ? (
					<ScreenShareVideo
						callID={callID}
						userID={selectedUser.userID || ""}
						style={{ backgroundColor: "black", width: "80%", height: "100%", borderRadius: "20px" }}
					/>
				) : selectedUser?.isScreen === false ? (
					<UserVideoWithAvatar
						key={selectedUser.userID}
						callID={callID}
						userID={selectedUser.userID || ""}
						kind="people_80"
						style={{
							width: "100%",
							height: "100%"
						}}
						fullScreen={true}
					/>
				) : null}
			</Row>
		</S.FullSizeWrapper>
	);
}

ScreenShare.displayName = "ScreenShare";
export default withRenderDetect(ScreenShare);

const StyledLottieButton = styled(Button)<{ disabled: boolean }>`
	path {
		fill: ${({ disabled }) => (disabled ? "" : "white")};
		opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
	}
`;

const Delimiter = styled.div`
	height: 14px;
	border-right: 1px solid ${({ theme }) => theme.colors.neutral_00_32};
	margin: 0 4px;
`;
